import { useState, useRef, useEffect } from 'react';

export default function ControllableStates({
  options = [],
  multi = false,
  value,
  setValue,
  label,
  placeholder = '',
  searchMethod,
}) {
  const [inputValue, setInputValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef();

  const getOptionLabel = (opt) => {
    console.log('getOptionLabel - opt:', opt)
    return opt?.[label] || opt.name;
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keep filteredOptions in sync when the source options change
  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  // Update input when value changes (single select)
  useEffect(() => {
    if (!multi && value) {
      setInputValue(getOptionLabel(value));
    }
  }, [value, multi]);

  // Filter locally OR trigger remote search
  useEffect(() => {
    if (searchMethod) {
      // Parent is expected to update `options`; we just open the menu.
      searchMethod(inputValue);
      setShowDropdown(true);
    } else {
      console.log('inputValue: ', inputValue)
      const lower = inputValue.toLowerCase();
      setFilteredOptions(
        options.filter((opt) => {
          const labelVal = opt[label] || `${opt.start_date} to ${opt.end_date}`;
          return labelVal?.toString().toLowerCase().includes(lower);
        })
      );
    }
  }, [inputValue, options, searchMethod, label]);

  const isSelected = (option) => {
    if (multi) {
      return Array.isArray(value) && value.some((v) => v === option);
    } else {
      return value === option;
    }
  };

  const toggleSelect = (option) => {
    if (multi) {
      if (isSelected(option)) {
        setValue(value.filter((v) => v !== option));
      } else {
        setValue([...(value || []), option]);
      }
      setInputValue('');
    } else {
      setValue(option);
      setInputValue(getOptionLabel(option));
      setShowDropdown(false);
    }
  };

  const clearSelection = () => {
    setValue(multi ? [] : null);
    setInputValue('');
    setShowDropdown(false);
    setFilteredOptions(options);
  };

  return (
    <div className="w-full relative" ref={containerRef}>
      <div className="relative">
        <input
          type="text"
          className="w-full border border-gray-300 rounded-md p-2 pr-8 focus:outline-none focus:ring-0"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => {
            setFilteredOptions(options);
            setShowDropdown(true);
          }}
        />
        {((multi && (value?.length ?? 0) > 0) || (!multi && value)) && (
          <button
            type="button"
            onClick={clearSelection}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title="Clear selection"
          >
            ✕
          </button>
        )}
      </div>

      {showDropdown && (
        <ul className="absolute z-10 bg-white border border-gray-300 rounded-md mt-1 w-full max-h-60 overflow-y-auto shadow-md">
          {filteredOptions.length === 0 ? (
            <li className="px-3 py-2 text-gray-500 text-sm">No results</li>
          ) : (
            filteredOptions.map((option) => (
              <li
                key={getOptionLabel(option)}
                onMouseDown={() => toggleSelect(option)}
                className={`px-3 py-2 cursor-pointer flex justify-between items-center hover:bg-gray-100 ${
                  isSelected(option) ? 'bg-yellow-100' : ''
                }`}
              >
                <span>{getOptionLabel(option)}</span>
                {isSelected(option) && (
                  <span className="text-yellow-500 text-sm ml-2">✓</span>
                )}
              </li>
            ))
          )}
        </ul>
      )}

      {multi && (value?.length ?? 0) > 0 && (
        <div className="flex flex-wrap mt-2 gap-2">
          {value.map((v, idx) => (
            <span
              key={idx}
              className="bg-yellow-100 text-yellow-600 px-2 py-1 rounded-full text-xs"
            >
              {getOptionLabel(v)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
